import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronLeft, FaChevronRight, FaCalendar } from 'react-icons/fa';

const CustomDatePicker = ({
  value = '',
  onChange,
  placeholder = 'dd/mm/aaaa',
  className = '',
  triggerClassName = '',
  disabled = false,
  minDate = null,
  maxDate = null,
  label = '',
  compact = false,
  usePortal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [position, setPosition] = useState({ vertical: 'bottom', horizontal: 'left' });
  const [popupCoords, setPopupCoords] = useState({ top: 0, left: 0, width: 0 });
  const datePickerRef = useRef(null);
  const popupRef = useRef(null);
  const inputRef = useRef(null);

  const calendarHeight = compact ? 268 : 380;
  const calendarWidth = compact ? 236 : 280;

  useEffect(() => {
    if (value) {
      const [year, month, date] = value.split('-').map(Number);
      const dateObj = new Date(year, month - 1, date);
      if (!isNaN(dateObj.getTime())) {
        setCurrentMonth(dateObj);
      }
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen || !inputRef.current) return;

    const rect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    let verticalPos = 'bottom';
    if (spaceBelow < calendarHeight && spaceAbove > spaceBelow) {
      verticalPos = 'top';
    }

    let horizontalPos = 'left';
    const spaceRight = viewportWidth - rect.left;
    const spaceLeft = rect.right;

    if (spaceRight < calendarWidth && spaceLeft > spaceRight) {
      horizontalPos = 'right';
    }

    setPosition({ vertical: verticalPos, horizontal: horizontalPos });

    const left = horizontalPos === 'right'
      ? Math.max(8, rect.right - calendarWidth)
      : Math.min(rect.left, viewportWidth - calendarWidth - 8);

    const top = verticalPos === 'top'
      ? rect.top - calendarHeight - 4
      : rect.bottom + 4;

    setPopupCoords({
      top: Math.max(8, top),
      left: Math.max(8, left),
      width: calendarWidth,
    });
  }, [isOpen, calendarHeight, calendarWidth]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInside = datePickerRef.current?.contains(event.target)
        || popupRef.current?.contains(event.target);
      if (!clickedInside) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getMonthName = (date) => {
    const months = [
      'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
    ];
    return months[date.getMonth()];
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const days = [];

    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: prevMonthDays - i,
        isCurrentMonth: false,
        fullDate: new Date(year, month - 1, prevMonthDays - i),
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: day,
        isCurrentMonth: true,
        fullDate: new Date(year, month, day),
      });
    }

    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: day,
        isCurrentMonth: false,
        fullDate: new Date(year, month + 1, day),
      });
    }

    return days;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length !== 3) return '';
    const [year, month, day] = parts.map(Number);
    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) return '';

    const dayStr = String(date.getDate()).padStart(2, '0');
    const monthStr = String(date.getMonth() + 1).padStart(2, '0');
    const yearStr = date.getFullYear();
    return `${dayStr}/${monthStr}/${yearStr}`;
  };

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateDisabled = (day) => {
    if (!day.isCurrentMonth) return true;

    if (minDate) {
      const [minYear, minMonth, minDateNum] = minDate.split('-').map(Number);
      const min = new Date(minYear, minMonth - 1, minDateNum);
      min.setHours(0, 0, 0, 0);
      const dayDate = new Date(day.fullDate);
      dayDate.setHours(0, 0, 0, 0);
      if (dayDate < min) return true;
    }

    if (maxDate) {
      const [maxYear, maxMonth, maxDateNum] = maxDate.split('-').map(Number);
      const max = new Date(maxYear, maxMonth - 1, maxDateNum);
      max.setHours(23, 59, 59, 999);
      const dayDate = new Date(day.fullDate);
      dayDate.setHours(0, 0, 0, 0);
      if (dayDate > max) return true;
    }

    return false;
  };

  const handleDateSelect = (day) => {
    if (day.isCurrentMonth && !isDateDisabled(day)) {
      onChange(formatDateForInput(day.fullDate));
      setIsOpen(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToPreviousYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1));
  };

  const goToNextYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1));
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    onChange(formatDateForInput(today));
    setIsOpen(false);
  };

  const clearDate = () => {
    onChange('');
    setIsOpen(false);
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day.fullDate.getDate() === today.getDate()
      && day.fullDate.getMonth() === today.getMonth()
      && day.fullDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day) => {
    if (!value || !day.isCurrentMonth) return false;
    const [year, month, date] = value.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, date);
    return (
      day.fullDate.getDate() === selectedDate.getDate()
      && day.fullDate.getMonth() === selectedDate.getMonth()
      && day.fullDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
  const dayButtonClass = compact ? 'w-7 h-7 text-xs' : 'w-8 h-8 text-sm';

  const calendarPopup = (
    <div
      ref={popupRef}
      className={`
        bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl
        ${compact ? 'p-2 min-w-[236px]' : 'p-4 min-w-[280px]'}
        ${usePortal ? 'fixed z-[10000]' : `absolute z-[9999] ${position.vertical === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'} ${position.horizontal === 'right' ? 'right-0' : 'left-0'}`}
      `}
      style={usePortal ? {
        top: popupCoords.top,
        left: popupCoords.left,
        width: popupCoords.width,
      } : undefined}
    >
      <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-4'}`}>
        <button
          type="button"
          onClick={goToPreviousYear}
          className={`${compact ? 'p-1' : 'p-1'} hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors`}
          aria-label="Ano anterior"
        >
          <FaChevronLeft className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-gray-600 dark:text-gray-400`} />
        </button>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={goToPreviousMonth}
            className={`${compact ? 'p-1' : 'p-2'} hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors`}
            aria-label="Mês anterior"
          >
            <FaChevronLeft className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-gray-600 dark:text-gray-400`} />
          </button>
          <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-900 dark:text-white capitalize px-1`}>
            {getMonthName(currentMonth)} {currentMonth.getFullYear()}
          </span>
          <button
            type="button"
            onClick={goToNextMonth}
            className={`${compact ? 'p-1' : 'p-2'} hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors`}
            aria-label="Próximo mês"
          >
            <FaChevronRight className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-gray-600 dark:text-gray-400`} />
          </button>
        </div>

        <button
          type="button"
          onClick={goToNextYear}
          className={`${compact ? 'p-1' : 'p-1'} hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors`}
          aria-label="Próximo ano"
        >
          <FaChevronRight className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-gray-600 dark:text-gray-400`} />
        </button>
      </div>

      <div className={`grid grid-cols-7 ${compact ? 'gap-0.5 mb-1' : 'gap-1 mb-2'}`}>
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={`text-center ${compact ? 'text-[10px] py-0.5' : 'text-xs py-1'} font-medium text-gray-500 dark:text-gray-400`}
          >
            {day}
          </div>
        ))}
      </div>

      <div className={`grid grid-cols-7 ${compact ? 'gap-0.5' : 'gap-1'}`}>
        {days.map((day, index) => {
          const dayDisabled = isDateDisabled(day);
          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDateSelect(day)}
              disabled={dayDisabled}
              className={`
                ${dayButtonClass} rounded transition-colors
                ${dayDisabled
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50'
                  : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }
                ${isToday(day) && day.isCurrentMonth && !dayDisabled ? 'font-bold border border-blue-500' : ''}
                ${isSelected(day) ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
              `}
            >
              {day.date}
            </button>
          );
        })}
      </div>

      <div className={`flex items-center justify-between ${compact ? 'mt-2 pt-2' : 'mt-4 pt-4'} border-t border-gray-200 dark:border-gray-600`}>
        <button
          type="button"
          onClick={clearDate}
          className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-blue-500 hover:text-blue-600 transition-colors`}
        >
          Limpar
        </button>
        <button
          type="button"
          onClick={goToToday}
          className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-blue-500 hover:text-blue-600 transition-colors`}
        >
          Hoje
        </button>
      </div>
    </div>
  );

  return (
    <div ref={datePickerRef} className={`relative ${className}`}>
      <div
        ref={inputRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          w-full px-4 border border-[#cfd4dd] dark:border-gray-600 rounded-lg
          bg-white dark:bg-gray-700 text-gray-900 dark:text-white
          focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
          flex items-center justify-between cursor-pointer
          transition-colors
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600'}
          ${triggerClassName || 'h-[46px] text-[15px]'}
        `}
      >
        <span className={value ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
          {value ? formatDate(value) : (label || placeholder)}
        </span>
        <FaCalendar className="w-4 h-4 text-gray-400 dark:text-gray-400" />
      </div>

      {isOpen && (usePortal ? createPortal(calendarPopup, document.body) : calendarPopup)}
    </div>
  );
};

export default CustomDatePicker;
